import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { queryClient } from "@/lib/queryClient";
import {
  IDLE_WARNING_MS,
  MAX_WARNING_MS,
  clearActivity,
  clearUserLocalData,
  markActivity,
  readLastActivity,
  rememberSignOutReason,
  sessionClock,
  signedInAt,
  type ExpiryReason,
} from "@/lib/sessionPolicy";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const ACTIVITY_EVENTS = ["pointerdown", "keydown", "wheel", "touchstart", "scroll", "pointermove"] as const;
const WRITE_EVERY_MS = 15_000;
const TICK_MS = 15_000;

const fmt = (ms: number) => {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return s >= 60 ? `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}` : `${s} seconds`;
};

/**
 * Ends a sign-in after 8 hours without activity or 12 hours after it began
 * (src/lib/sessionPolicy.ts), with a warning first. Renders nothing while
 * signed out.
 */
export function SessionGuard() {
  const navigate = useNavigate();
  const [session, setSession] = useState<Session | null>(null);
  const [warning, setWarning] = useState<{ reason: ExpiryReason; remaining: number } | null>(null);
  const lastWrite = useRef(0);
  const endingRef = useRef(false);
  // The 12-hour notice cannot be postponed, so once read it stays closed.
  const maxNoticeSeen = useRef(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSession(data.session));
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, s) => {
      // supabase-js also reports SIGNED_IN when a tab regains focus; only a
      // sign-in that just happened starts the clocks again.
      const fresh = (signedInAt(s?.user) ?? 0) > Date.now() - 2 * 60 * 1000;
      if (event === "SIGNED_IN" && fresh) { markActivity(); maxNoticeSeen.current = false; }
      if (event === "SIGNED_OUT") { clearActivity(); setWarning(null); }
      setSession(s);
    });
    return () => subscription.unsubscribe();
  }, []);

  const end = useCallback(async (reason: ExpiryReason) => {
    if (endingRef.current) return;
    endingRef.current = true;
    setWarning(null);
    clearUserLocalData();
    clearActivity();
    rememberSignOutReason(reason);
    queryClient.clear();
    try {
      // "local": this browser only; the refresh token is revoked on the server.
      await supabase.auth.signOut({ scope: "local" });
    } finally {
      endingRef.current = false;
      navigate("/login", { replace: true });
    }
  }, [navigate]);

  const check = useCallback(() => {
    if (!session) return;
    const clock = sessionClock(readLastActivity(), signedInAt(session.user));
    if (clock.remaining <= 0) {
      void end(clock.reason);
      return;
    }
    const warnAt = clock.reason === "idle" ? IDLE_WARNING_MS : MAX_WARNING_MS;
    const show = clock.remaining <= warnAt && !(clock.reason === "expired" && maxNoticeSeen.current);
    setWarning(show ? clock : null);
  }, [session, end]);

  // Activity in this tab counts for every tab (the timestamp is shared).
  useEffect(() => {
    if (!session) return;
    if (readLastActivity() == null) markActivity();
    const onActivity = () => {
      const now = Date.now();
      if (now - lastWrite.current < WRITE_EVERY_MS) return;
      lastWrite.current = now;
      markActivity(now);
    };
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, onActivity, { passive: true, capture: true }));
    return () => ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, onActivity, { capture: true }));
  }, [session]);

  // Timers are slowed down in background tabs and stop while the computer
  // sleeps, so the check also runs whenever the tab comes back.
  useEffect(() => {
    if (!session) return;
    check();
    const id = window.setInterval(check, warning ? 1000 : TICK_MS);
    const onBack = () => { if (document.visibilityState === "visible") check(); };
    document.addEventListener("visibilitychange", onBack);
    window.addEventListener("focus", onBack);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onBack);
      window.removeEventListener("focus", onBack);
    };
  }, [session, check, warning]);

  if (!session || !warning) return null;

  const idle = warning.reason === "idle";
  return (
    <AlertDialog open>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{idle ? "Are you still there?" : "Your session is ending"}</AlertDialogTitle>
          <AlertDialogDescription>
            {idle
              ? `For your security you will be signed out in ${fmt(warning.remaining)} because there has been no activity for a while.`
              : `For your security a sign-in lasts 12 hours. You will be signed out in ${fmt(warning.remaining)}, so save anything you are working on.`}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => void end(warning.reason)}>Sign out now</AlertDialogCancel>
          {idle ? (
            <AlertDialogAction onClick={() => { markActivity(); lastWrite.current = Date.now(); setWarning(null); }}>
              Stay signed in
            </AlertDialogAction>
          ) : (
            <AlertDialogAction onClick={() => { maxNoticeSeen.current = true; setWarning(null); }}>OK</AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default SessionGuard;
